import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../config";
import { useLanguage } from "../../contexts/LanguageContext";
import { Share2, Link2, Check, Clock, X, ExternalLink, Gift, AlertCircle } from "lucide-react";

const SITE_URL = "https://toprankpulse.com";

const SHARE_PLATFORMS = [
  { id: "twitter", label: "X (Twitter)", getUrl: (text) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(SITE_URL)}` },
  { id: "facebook", label: "Facebook", getUrl: () => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SITE_URL)}` },
  { id: "linkedin", label: "LinkedIn", getUrl: () => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(SITE_URL)}` },
  { id: "instagram", label: "Instagram", getUrl: () => "https://www.instagram.com/" },
];

function StatusBadge({ status, t }) {
  const map = {
    pending: { cls: "bg-yellow-100 text-yellow-800", label: t.ref_bl_pending || "Pending" },
    approved: { cls: "bg-emerald-100 text-emerald-800", label: t.ref_bl_approved || "Approved" },
    rejected: { cls: "bg-red-100 text-red-800", label: t.ref_bl_rejected || "Rejected" },
  };
  const s = map[status] || map.pending;
  return <span className={`text-[10px] px-2 py-0.5 font-bold ${s.cls}`}>{s.label}</span>;
}

export default function ReferralTab() {
  const { t } = useLanguage();
  const [shareStatus, setShareStatus] = useState({ share_count: 0, can_claim: false, claimed: false, claim_status: null });
  const [backlinkStatus, setBacklinkStatus] = useState({ backlinks: [], total_reward_days: 0 });
  const [backlinkUrl, setBacklinkUrl] = useState("");
  const [proofUrl, setProofUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [blError, setBlError] = useState("");
  const [blSuccess, setBlSuccess] = useState("");

  const fetchStatus = useCallback(async () => {
    try {
      const [shareRes, blRes] = await Promise.all([
        axios.get(`${API}/referral/share/status`, { withCredentials: true }),
        axios.get(`${API}/referral/backlink/status`, { withCredentials: true }),
      ]);
      setShareStatus(shareRes.data);
      setBacklinkStatus(blRes.data);
    } catch {}
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleShare = async (platform) => {
    const shareText = (t.share_text || "My business is losing €{amount}/month without AI visibility!").replace("{amount}", "5,780");
    const p = SHARE_PLATFORMS.find(pl => pl.id === platform);
    if (p) window.open(p.getUrl(shareText), "_blank", "noopener");
    try {
      await axios.post(`${API}/referral/share/track-platform`, { platform }, { withCredentials: true });
      const res = await axios.get(`${API}/referral/share/status`, { withCredentials: true });
      setShareStatus(res.data);
    } catch {}
  };

  const handleClaim = async () => {
    setClaimSubmitting(true);
    try {
      await axios.post(`${API}/referral/share/claim`, { proof_url: proofUrl }, { withCredentials: true });
      setShareStatus(prev => ({ ...prev, claimed: true, claim_status: "pending" }));
    } catch {}
    setClaimSubmitting(false);
  };

  const handleBacklinkSubmit = async () => {
    setBlError(""); setBlSuccess(""); setSubmitting(true);
    try {
      const res = await axios.post(`${API}/referral/backlink/submit`, { page_url: backlinkUrl }, { withCredentials: true });
      setBlSuccess(res.data.message);
      setBacklinkUrl("");
      fetchStatus();
    } catch (e) {
      setBlError(e.response?.data?.detail || "Error");
    }
    setSubmitting(false);
  };

  const shareCount = shareStatus.share_count || 0;
  const progress = Math.min(shareCount / 5, 1) * 100;

  return (
    <div className="max-w-3xl space-y-8" data-testid="referral-tab">
      <div>
        <h2 className="text-2xl font-black font-['Chivo'] text-gray-900 flex items-center gap-2">
          <Gift className="w-6 h-6 text-blue-600" />
          {t.ref_title || "Earn Free Service"}
        </h2>
        <p className="text-gray-500 mt-1">{t.ref_subtitle || "Share or link to us and get free premium access"}</p>
      </div>

      {/* --- SHARE 5x PROGRAM --- */}
      <div className="bg-white border border-gray-200 p-6 space-y-5" data-testid="share-program">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Share2 className="w-5 h-5 text-blue-600" />
              {t.ref_share_title || "Share 5x = 1 Week Free"}
            </h3>
            <p className="text-sm text-gray-500 mt-1">{t.ref_share_desc || "Share TopRankPulse on social media 5 times and get 1 week of premium access for free."}</p>
          </div>
          <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 font-bold whitespace-nowrap">{t.ref_week_free || "1 week free"}</span>
        </div>

        {/* Progress */}
        <div>
          <div className="flex justify-between text-xs text-gray-500 mb-1.5">
            <span>{shareCount} {t.ref_share_progress || "shares"}</span>
            <span>{shareCount} {t.ref_share_of || "of"} 5 {t.ref_share_needed || "needed"}</span>
          </div>
          <div className="w-full h-2.5 bg-gray-100 overflow-hidden">
            <div className="h-full bg-blue-600 transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Share buttons */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {SHARE_PLATFORMS.map(p => (
            <button
              key={p.id}
              data-testid={`ref-share-${p.id}`}
              onClick={() => handleShare(p.id)}
              className="flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium border border-gray-200 text-gray-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              {p.label}
            </button>
          ))}
        </div>

        {/* Claim section */}
        {shareStatus.can_claim && !shareStatus.claimed && (
          <div className="bg-blue-50 border border-blue-200 p-4 space-y-3" data-testid="share-claim-form">
            <p className="text-sm font-medium text-blue-800">{t.ref_share_proof || "Add proof (screenshot URL or link)"}</p>
            <input
              data-testid="share-proof-input"
              type="text" value={proofUrl} onChange={e => setProofUrl(e.target.value)}
              placeholder="https://twitter.com/you/status/..."
              className="w-full px-3 py-2 text-sm border border-blue-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              data-testid="share-claim-btn"
              onClick={handleClaim}
              disabled={claimSubmitting}
              className="px-4 py-2 text-sm font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {claimSubmitting ? <Clock className="w-4 h-4 animate-spin inline mr-1" /> : null}
              {t.ref_share_submit || "Submit for Review"}
            </button>
          </div>
        )}

        {shareStatus.claimed && (
          <div className="flex items-center gap-2 text-sm" data-testid="share-claim-status">
            {shareStatus.claim_status === "approved" && <><Check className="w-4 h-4 text-emerald-600" /><span className="text-emerald-700 font-medium">{t.ref_share_approved || "Approved!"}</span></>}
            {shareStatus.claim_status === "pending" && <><Clock className="w-4 h-4 text-yellow-600" /><span className="text-yellow-700 font-medium">{t.ref_share_pending || "Pending Review"}</span></>}
            {shareStatus.claim_status === "rejected" && <><X className="w-4 h-4 text-red-600" /><span className="text-red-700 font-medium">{t.ref_share_rejected || "Rejected"}</span></>}
          </div>
        )}
      </div>

      {/* --- BACKLINK PROGRAM --- */}
      <div className="bg-white border border-gray-200 p-6 space-y-5" data-testid="backlink-program">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Link2 className="w-5 h-5 text-emerald-600" />
              {t.ref_bl_title || "Backlink = 2 Weeks Free"}
            </h3>
            <p className="text-sm text-gray-500 mt-1">{t.ref_bl_desc || "Add a link to toprankpulse.com on your website. Each backlink from a unique domain = 2 weeks free premium."}</p>
          </div>
          <span className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 font-bold whitespace-nowrap">{t.ref_two_weeks || "2 weeks free"}</span>
        </div>

        {/* Submit form */}
        <div className="flex gap-2">
          <input
            data-testid="backlink-url-input"
            type="text" value={backlinkUrl} onChange={e => setBacklinkUrl(e.target.value)}
            placeholder={t.ref_bl_input || "URL of page with your backlink"}
            className="flex-1 px-3 py-2 text-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <button
            data-testid="backlink-submit-btn"
            onClick={handleBacklinkSubmit}
            disabled={submitting || !backlinkUrl.startsWith("http")}
            className="px-4 py-2 text-sm font-bold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap"
          >
            {submitting ? <Clock className="w-4 h-4 animate-spin inline mr-1" /> : null}
            {t.ref_bl_submit || "Submit Backlink"}
          </button>
        </div>

        {blError && <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{blError}</p>}
        {blSuccess && <p className="text-sm text-emerald-600 flex items-center gap-1"><Check className="w-3.5 h-3.5" />{blSuccess}</p>}

        {/* Submitted backlinks */}
        {backlinkStatus.backlinks.length > 0 && (
          <div className="space-y-2">
            {backlinkStatus.backlinks.map((bl, i) => (
              <div key={i} className="flex items-center justify-between py-2 px-3 bg-gray-50 text-sm" data-testid={`backlink-item-${i}`}>
                <div className="min-w-0">
                  <p className="text-gray-800 font-medium truncate">{bl.domain}</p>
                  <p className="text-gray-400 text-xs truncate">{bl.page_url}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {bl.auto_verified && <span className="text-[10px] text-emerald-600 font-bold">{t.ref_bl_verify || "Auto-verified"}</span>}
                  <StatusBadge status={bl.status} t={t} />
                </div>
              </div>
            ))}
            {backlinkStatus.total_reward_days > 0 && (
              <p className="text-sm font-bold text-emerald-700" data-testid="backlink-total-days">
                {t.ref_bl_total || "Total free days earned"}: {backlinkStatus.total_reward_days} {t.ref_bl_days || "days"}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
